/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { identityProviderConnectionsCreate } from "../funcs/identityProviderConnectionsCreate.js";
import { identityProviderConnectionsGet } from "../funcs/identityProviderConnectionsGet.js";
import { identityProviderConnectionsListApplications } from "../funcs/identityProviderConnectionsListApplications.js";
import { identityProviderConnectionsRecordAgent } from "../funcs/identityProviderConnectionsRecordAgent.js";
import { identityProviderConnectionsReplaceClientSecret } from "../funcs/identityProviderConnectionsReplaceClientSecret.js";
import { identityProviderConnectionsRevoke } from "../funcs/identityProviderConnectionsRevoke.js";
import { identityProviderConnectionsSetSetupMethod } from "../funcs/identityProviderConnectionsSetSetupMethod.js";
import { identityProviderConnectionsSubmitClientId } from "../funcs/identityProviderConnectionsSubmitClientId.js";
import { identityProviderConnectionsSyncApplications } from "../funcs/identityProviderConnectionsSyncApplications.js";
import { identityProviderConnectionsVerify } from "../funcs/identityProviderConnectionsVerify.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { GetIdentityProviderConnectionResult } from "../models/components/getidentityproviderconnectionresult.js";
import { ListIdentityProviderConnectionApplicationsResult } from "../models/components/listidentityproviderconnectionapplicationsresult.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import {
  CreateIdentityProviderConnectionRequest,
  CreateIdentityProviderConnectionSecurity,
} from "../models/operations/createidentityproviderconnection.js";
import {
  GetIdentityProviderConnectionRequest,
  GetIdentityProviderConnectionSecurity,
} from "../models/operations/getidentityproviderconnection.js";
import {
  ListIdentityProviderConnectionApplicationsRequest,
  ListIdentityProviderConnectionApplicationsSecurity,
} from "../models/operations/listidentityproviderconnectionapplications.js";
import {
  RecordIdentityProviderConnectionAgentRequest,
  RecordIdentityProviderConnectionAgentSecurity,
} from "../models/operations/recordidentityproviderconnectionagent.js";
import {
  ReplaceIdentityProviderConnectionClientSecretRequest,
  ReplaceIdentityProviderConnectionClientSecretSecurity,
} from "../models/operations/replaceidentityproviderconnectionclientsecret.js";
import {
  RevokeIdentityProviderConnectionRequest,
  RevokeIdentityProviderConnectionSecurity,
} from "../models/operations/revokeidentityproviderconnection.js";
import {
  SetIdentityProviderConnectionSetupMethodRequest,
  SetIdentityProviderConnectionSetupMethodSecurity,
} from "../models/operations/setidentityproviderconnectionsetupmethod.js";
import {
  SubmitIdentityProviderConnectionClientIdRequest,
  SubmitIdentityProviderConnectionClientIdSecurity,
} from "../models/operations/submitidentityproviderconnectionclientid.js";
import {
  SyncIdentityProviderConnectionApplicationsRequest,
  SyncIdentityProviderConnectionApplicationsSecurity,
} from "../models/operations/syncidentityproviderconnectionapplications.js";
import {
  VerifyIdentityProviderConnectionRequest,
  VerifyIdentityProviderConnectionSecurity,
} from "../models/operations/verifyidentityproviderconnection.js";
import { unwrapAsync } from "../types/fp.js";

export class IdentityProviderConnections extends ClientSDK {
  /**
   * create identityProviderConnections
   *
   * @remarks
   * Create the organization's Okta connection. Discovers the org's authorization server, provisions a signing key and JWKS URL, and returns the console checklist. Requires org:admin and the okta-connections rollout. One live connection per organization; creation is rate limited.
   */
  async create(
    request: CreateIdentityProviderConnectionRequest,
    security?: CreateIdentityProviderConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * get identityProviderConnections
   *
   * @remarks
   * Get a connection by ID, or the organization's live Okta connection when no ID is given. Session only; requires org:read.
   */
  async get(
    request?: GetIdentityProviderConnectionRequest | undefined,
    security?: GetIdentityProviderConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<GetIdentityProviderConnectionResult> {
    return unwrapAsync(identityProviderConnectionsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listApplications identityProviderConnections
   *
   * @remarks
   * List the applications snapshot for the connection with live assignment counts and the last reconcile run. The connection must be verified. Requires org:admin.
   */
  async listApplications(
    request: ListIdentityProviderConnectionApplicationsRequest,
    security?: ListIdentityProviderConnectionApplicationsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListIdentityProviderConnectionApplicationsResult> {
    return unwrapAsync(identityProviderConnectionsListApplications(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recordAgent identityProviderConnections
   *
   * @remarks
   * Record the Okta AI agent ID and the application it is bound to, for display. Okta does not expose these through its API. Requires org:admin.
   */
  async recordAgent(
    request: RecordIdentityProviderConnectionAgentRequest,
    security?: RecordIdentityProviderConnectionAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsRecordAgent(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * replaceClientSecret identityProviderConnections
   *
   * @remarks
   * Replace the client secret of a connection installed from the Okta Integration Network and re-verify it. The previous secret is kept if Okta rejects the new one. Requires org:admin.
   */
  async replaceClientSecret(
    request: ReplaceIdentityProviderConnectionClientSecretRequest,
    security?:
      | ReplaceIdentityProviderConnectionClientSecretSecurity
      | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsReplaceClientSecret(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revoke identityProviderConnections
   *
   * @remarks
   * Revoke the connection: withdraw every signing key from the JWKS, disable the key material, and tombstone the connection so a new one can be created. Idempotent. Requires org:admin.
   */
  async revoke(
    request: RevokeIdentityProviderConnectionRequest,
    security?: RevokeIdentityProviderConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsRevoke(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setSetupMethod identityProviderConnections
   *
   * @remarks
   * Switch how a pending connection connects to Okta: an install from the Okta Integration Network (client secret) or a custom API Services app (private key). Allowed until the client ID is submitted. Switching to the custom app provisions a signing key and JWKS URL once; switching away stops serving the key and switching back reuses it. A key left unused is retired when the client ID is submitted or the connection is revoked. Requires org:admin.
   */
  async setSetupMethod(
    request: SetIdentityProviderConnectionSetupMethodRequest,
    security?: SetIdentityProviderConnectionSetupMethodSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsSetSetupMethod(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * submitClientId identityProviderConnections
   *
   * @remarks
   * Record the client ID of the Okta API Services application and verify it. Connections installed from the Okta Integration Network also take the client secret. Allowed once, while the connection is pending; revoke and recreate to change it. Requires org:admin.
   */
  async submitClientId(
    request: SubmitIdentityProviderConnectionClientIdRequest,
    security?: SubmitIdentityProviderConnectionClientIdSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsSubmitClientId(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * syncApplications identityProviderConnections
   *
   * @remarks
   * Run the applications snapshot on the next coordinator pass, within minutes, instead of at the next scheduled interval. The connection must be verified. Rate limited per organization. Requires org:admin.
   */
  async syncApplications(
    request: SyncIdentityProviderConnectionApplicationsRequest,
    security?: SyncIdentityProviderConnectionApplicationsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsSyncApplications(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * verify identityProviderConnections
   *
   * @remarks
   * Re-verify the connection against Okta: mint a token, confirm each required scope with a read, and record the outcome. Rate limited per organization. Requires org:admin.
   */
  async verify(
    request: VerifyIdentityProviderConnectionRequest,
    security?: VerifyIdentityProviderConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<OktaIdentityProviderConnection> {
    return unwrapAsync(identityProviderConnectionsVerify(
      this,
      request,
      security,
      options,
    ));
  }
}
